import React from 'react';
import './Skills.css';
import {
  FaCode,
  FaChartBar,
  FaChartLine,
  FaCloud,
  FaRobot,
  FaTools,
  FaTrophy,
  FaExternalLinkAlt
} from 'react-icons/fa';
import biAnalyticsCert from '../assets/certificates/business-intelligence-analytics.pdf';
import uipathCert from '../assets/certificates/uipath-automation-developer-associate.pdf';

const Skills = () => {
  const skillCategories = [
    {
      title: 'Programming',
      icon: <FaCode />,
      accent: 'var(--accent-cyan)',
      skills: ['Python', 'SQL']
    },
    {
      title: 'Libraries',
      icon: <FaChartBar />,
      accent: 'var(--accent-teal)',
      skills: ['NumPy', 'Pandas', 'Scikit-learn', 'TensorFlow', 'Matplotlib']
    },
    {
      title: 'Tools',
      icon: <FaChartLine />,
      accent: 'var(--accent-orange)',
      skills: ['Power BI', 'Tableau', 'UiPath', 'Excel']
    },
    {
      title: 'Analytics',
      icon: <FaCloud />,
      accent: 'var(--accent-purple)',
      skills: ['Data Analytics', 'Business Intelligence', 'Data Visualization', 'Statistics']
    },
    {
      title: 'ML',
      icon: <FaRobot />,
      accent: 'var(--accent-cyan)',
      skills: ['Machine Learning', 'Forecasting', 'ARIMA', 'NER', '3D U-Net']
    },
    {
      title: 'Workflow',
      icon: <FaTools />,
      accent: 'var(--accent-teal)',
      skills: ['Data Cleaning', 'Visualization', 'Automation', 'Reporting']
    }
  ];

  const certifications = [
    {
      name: 'Business Intelligence & Analytics',
      issuer: 'NPTEL - IITM',
      year: '2025',
      file: biAnalyticsCert
    },
    {
      name: 'UiPath Automation Developer Associate',
      issuer: 'UiPath',
      year: '2025',
      file: uipathCert
    },
    { name: 'Introduction to IoT', issuer: 'NPTEL', year: '2024', file: null },
    { name: 'AI for Everyone', issuer: 'Coursera', year: '2024', file: null }
  ];

  const coreStack = ['Python', 'SQL', 'Power BI', 'Tableau', 'Pandas', 'TensorFlow', 'Forecasting', 'Business Intelligence'];

  return (
    <section id="skills" className="skills section">
      <div className="container">
        <h2 className="section-title">Technical Skills</h2>
        <p className="section-subtitle">
          Tools and methods I use for analytics, automation, and machine learning delivery.
        </p>

        <div className="skills-spotlight">
          <div className="skills-copy">
            <span className="skills-kicker">Core Stack</span>
            <h3>From raw data to real outcomes.</h3>
            <p>
              Not just individual tools — clean end-to-end workflows across extraction, analysis, modeling, and visualization.
            </p>
          </div>

          <div className="core-stack">
            {coreStack.map((item) => (
              <span key={item} className="stack-pill">{item}</span>
            ))}
          </div>
        </div>

        <div className="skills-stream">
          {skillCategories.map((category) => (
            <article key={category.title} className="skill-lane" style={{ '--lane-accent': category.accent }}>
              <div className="lane-header">
                <span className="lane-icon">{category.icon}</span>
                <div>
                  <h3 className="lane-title">{category.title}</h3>
                  <p className="lane-meta">{category.skills.length} tools</p>
                </div>
              </div>

              <div className="lane-skills">
                {category.skills.map((skill) => (
                  <span key={skill} className="lane-pill">{skill}</span>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="certifications-section">
          <div className="certifications-header">
            <span className="skills-kicker">Credentials</span>
            <h3 className="certifications-title">Certifications</h3>
          </div>

          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card">
                <div className="cert-icon"><FaTrophy /></div>
                <div className="cert-content">
                  <h4 className="cert-name">{cert.name}</h4>
                  <p className="cert-issuer">{cert.issuer}</p>
                </div>
                <div className="cert-meta">
                  <div className="cert-year">{cert.year}</div>
                  {cert.file ? (
                    <a className="cert-link" href={cert.file} target="_blank" rel="noopener noreferrer">
                      View PDF <FaExternalLinkAlt />
                    </a>
                  ) : (
                    <span className="cert-link cert-link-muted">Coming soon</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
